import React from 'react';
import {
  XMarkIcon,
  TruckIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  ArrowPathIcon,
  MapPinIcon,
  CalendarIcon,
  HashtagIcon,
  UserIcon,
  Cog6ToothIcon
} from '@heroicons/react/24/outline';
import Modal from '../ui/Modal';
import { Vehiculo } from '../../services/vehiculosService';

interface ModalDetallesVehiculoProps {
  isOpen: boolean;
  onClose: () => void;
  vehiculo: Vehiculo | null;
}

const ModalDetallesVehiculo: React.FC<ModalDetallesVehiculoProps> = ({
  isOpen,
  onClose,
  vehiculo,
}) => {
  if (!vehiculo) return null;

  const obtenerInfoEstado = (estado: string) => {
    switch (estado) {
      case 'disponible':
        return { icono: CheckCircleIcon, color: 'text-green-500', bg: 'bg-green-100 dark:bg-green-900/40', label: 'Disponible' };
      case 'en_ruta':
        return { icono: ArrowPathIcon, color: 'text-blue-500', bg: 'bg-blue-100 dark:bg-blue-900/40', label: 'En Ruta' };
      case 'mantenimiento':
        return { icono: ExclamationTriangleIcon, color: 'text-yellow-500', bg: 'bg-yellow-100 dark:bg-yellow-900/40', label: 'Mantenimiento' };
      case 'fuera_servicio':
        return { icono: ClockIcon, color: 'text-red-500', bg: 'bg-red-100 dark:bg-red-900/40', label: 'Fuera de Servicio' };
      default:
        return { icono: ClockIcon, color: 'text-gray-500', bg: 'bg-gray-100 dark:bg-gray-700', label: estado || 'Sin estado' };
    }
  };

  const estado = obtenerInfoEstado(vehiculo.estado);
  const IconoEstado = estado.icono;

  const Fila: React.FC<{ icono: any; label: string; valor?: string | number | null }> = ({ icono: Icono, label, valor }) => (
    <div className="flex items-start gap-3 py-3 border-b border-gray-100 dark:border-gray-700 last:border-0">
      <div className={`p-2 rounded-lg ${estado.bg}`}>
        <Icono className={`h-4 w-4 ${estado.color}`} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">{label}</p>
        <p className="text-sm font-semibold text-gray-900 dark:text-white mt-0.5 truncate">
          {valor ?? <span className="text-gray-400 dark:text-gray-500 italic">No disponible</span>}
        </p>
      </div>
    </div>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" title={`Detalles - ${vehiculo.placa}`}>
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <TruckIcon className="h-6 w-6 text-blue-600" />
            Detalles del Vehículo
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Banner superior */}
        <div className={`flex items-center gap-4 p-4 rounded-xl mb-6 ${estado.bg}`}>
          <div className="p-3 rounded-full bg-white/70 dark:bg-gray-800/70">
            <TruckIcon className={`h-8 w-8 ${estado.color}`} />
          </div>
          <div className="flex-1">
            <p className="text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wide">Placa</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">{vehiculo.placa}</p>
          </div>
          <div className="text-right">
            <p className="text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wide mb-1">Estado</p>
            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-semibold ${estado.bg} ${estado.color} border border-current/20`}>
              <IconoEstado className="h-4 w-4" />
              {estado.label}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Datos generales */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
            <h3 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wide mb-1 flex items-center gap-2">
              <Cog6ToothIcon className="h-4 w-4 text-blue-500" />
              Especificaciones
            </h3>
            <Fila icono={HashtagIcon} label="Marca" valor={vehiculo.marca} />
            <Fila icono={HashtagIcon} label="Modelo" valor={vehiculo.modelo} />
            <Fila icono={CalendarIcon} label="Año" valor={(vehiculo as any).anio ?? vehiculo.año} />
            <Fila icono={TruckIcon} label="Tipo" valor={vehiculo.tipo ? vehiculo.tipo.charAt(0).toUpperCase() + vehiculo.tipo.slice(1).replace('_', ' ') : undefined} />
            <Fila icono={HashtagIcon} label="VIN / Chasis" valor={(vehiculo as any).vin} />
          </div>

          {/* Operación */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
            <h3 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wide mb-1 flex items-center gap-2">
              <UserIcon className="h-4 w-4 text-indigo-500" />
              Operación
            </h3>
            <Fila icono={CheckCircleIcon} label="Capacidad de Carga" valor={vehiculo.capacidadCarga != null ? `${vehiculo.capacidadCarga.toLocaleString()} kg` : undefined} />
            <Fila icono={UserIcon} label="Conductor Asignado" valor={(vehiculo as any).conductor || 'Sin asignar'} />
            <Fila icono={MapPinIcon} label="Ubicación Actual" valor={(vehiculo as any).ubicacion || (vehiculo as any).direccion || 'No reportada'} />
            <Fila icono={CalendarIcon} label="Fecha Creación" valor={(vehiculo as any).fechaCreacion || (vehiculo as any).fecha_creacion} />
            <Fila icono={CalendarIcon} label="Última Actualización" valor={(vehiculo as any).fechaActualizacion || (vehiculo as any).fecha_actualizacion} />
          </div>
        </div>

        {/* Acciones rápidas */}
        <div className="flex justify-end gap-3 pt-6 mt-2 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors font-medium"
          >
            Cerrar
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ModalDetallesVehiculo;
